import { type EnumFrontendLayoutSetting } from '@Model';
import { useTableLayoutFromSettings } from '@Hooks/index';
import { type ILinkable, type ILinkedListProperties, LinkedList } from '@synqo/frontend-component-library';
import { useMemo } from 'react';

export interface LinkedListWithLayoutSettingsProps<T extends ILinkable> extends ILinkedListProperties<T> {
    layoutSettingsContext: EnumFrontendLayoutSetting;
    layoutSettingsModalContext: EnumFrontendLayoutSetting;
}

export function LinkedListWithLayoutSettings<T extends ILinkable>({
    layoutSettingsContext,
    layoutSettingsModalContext,
    ...props
}: LinkedListWithLayoutSettingsProps<T>) {
    const { columnSizing, columnVisibility, updateSizing, updateVisibility } = useTableLayoutFromSettings(
        layoutSettingsContext,
        props.columns,
    );

    const {
        columnSizing: modalColumnSizing,
        columnVisibility: modalColumnVisibility,
        updateSizing: updateModalSizing,
        updateVisibility: updateModalVisibility,
    } = useTableLayoutFromSettings(
        layoutSettingsModalContext,
        props.modalColumns?.length ? props.modalColumns : props.columns,
    );

    // Merged rather than passed through, so a caller that memoises its own tableProps keeps the
    // stable identity all the way to the table instead of losing it here.
    const tableProps = useMemo(
        () => ({
            ...props.tableProps,
            columnSizing,
            columnVisibility,
            onSizesChange: updateSizing,
            onVisibilityChange: updateVisibility,
        }),
        [props.tableProps, columnSizing, columnVisibility, updateSizing, updateVisibility],
    );

    const tableModalProps = useMemo(
        () => ({
            ...props.tableModalProps,
            columnSizing: modalColumnSizing,
            columnVisibility: modalColumnVisibility,
            onSizesChange: updateModalSizing,
            onVisibilityChange: updateModalVisibility,
        }),
        [props.tableModalProps, modalColumnSizing, modalColumnVisibility, updateModalSizing, updateModalVisibility],
    );

    return <LinkedList<T> {...props} tableProps={tableProps} tableModalProps={tableModalProps} />;
}
